import {
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STORES,
  type AdminSubscriptionView,
  type SubscriptionStatusName,
} from '@steado/admin-contract/billing';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, PersonCell, RowActionsCell, TextCell, type ValueTone } from '@/components/table/cells';
import type { ToneName } from '@/components/table/tone';
import { Button } from '@/components/ui/button';
import { ScopePills } from '@/components/table/ScopePills';
import { useAuth } from '@/lib/auth/auth-context';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { formatDate, formatDateTime, humanise } from '@/lib/format';

import { BillingEventDrawer } from './BillingEventDrawer';
import { BILLING_PAGE_SIZE, billingEventsQueryOptions, subscriptionsQueryOptions } from './billing-query';
import { BILLING_VIEWS, type BillingSearch, type BillingView } from './billing-search';

/**
 * The design names these after what an operator is actually checking. "Status
 * at the store" and "Access right now" are two different facts that disagree
 * often enough to be worth distinguishing in the header rather than only in
 * the cells — a cancelled subscription still has access until it lapses.
 */
const SUBSCRIPTION_COLUMNS: readonly TableColumn[] = [
  { key: 'user', label: 'Person', width: '1.5fr' },
  { key: 'status', label: 'Status at the store', width: '1fr' },
  { key: 'entitlement', label: 'Access right now', width: '1.1fr' },
  { key: 'store', label: 'Bought through', width: '1fr' },
  { key: 'renews', label: 'What happens next', width: '1.2fr' },
];

const EVENT_COLUMNS: readonly TableColumn[] = [
  { key: 'rcId', label: 'Their event id', width: '1.2fr' },
  { key: 'type', label: 'Type', width: '1.2fr' },
  { key: 'user', label: 'Who it was for', width: '1.2fr' },
  { key: 'when', label: 'Arrived', width: '0.9fr' },
  // Answered on the row rather than on the drawer: the operator's question
  // before opening one of these is whether opening it is worth the trip.
  { key: 'replayable', label: 'Can it be replayed', width: '1.7fr' },
  { key: 'actions', label: '', width: '106px', align: 'right' },
];

const STATUS_TONES: Readonly<Record<SubscriptionStatusName, ToneName>> = {
  active: 'ok',
  trialing: 'info',
  canceled: 'warn',
  expired: 'mute',
  billing_issue: 'err',
};

const VIEW_LABELS: Readonly<Record<BillingView, string>> = {
  subscriptions: 'Subscriptions',
  events: 'Webhook events',
};

/**
 * When the store's status and the access actually granted disagree.
 *
 * They legitimately differ — a cancellation keeps Premium until the period
 * ends — so this names only the two pairs that are a bug: paying with no
 * access, or access with nothing paying for it.
 */
function mismatchOf(row: AdminSubscriptionView): string | null {
  const paying = row.status === 'active' || row.status === 'trialing';
  if (paying && row.entitlement === 'free') return 'paying, but no access';
  if (row.status === 'expired' && row.entitlement === 'premium') return 'expired, but still premium';
  return null;
}

/** What happens to this subscription next, as a sentence rather than a date. */
function nextOf(row: AdminSubscriptionView): { value: string | null; tone?: ValueTone } {
  if (row.currentPeriodEnd === null) return { value: null };

  const when = formatDate(row.currentPeriodEnd);
  if (row.status === 'expired') return { value: `Lapsed ${when}`, tone: 'muted' };
  if (row.willRenew) return { value: `Renews ${when}` };
  return { value: `Ends ${when}`, tone: 'warn' };
}

export interface BillingScreenProps {
  readonly search: BillingSearch;
  readonly onSearchChange: (next: BillingSearch) => void;
}

export function BillingScreen({ search, onSearchChange }: BillingScreenProps) {
  const { api, can } = useAuth();
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const isEvents = search.view === 'events';

  const subscriptions = useQuery({
    ...subscriptionsQueryOptions(api, search),
    enabled: !isEvents,
  });
  const events = useQuery(billingEventsQueryOptions(api, search, isEvents));

  const active = isEvents ? events : subscriptions;
  const counts = useQuery(scopeCountsQueryOptions(api));

  const setFilter = useCallback(
    (patch: Partial<BillingSearch>) => {
      setCursorStack([]);
      onSearchChange({ ...search, ...patch, cursor: undefined });
    },
    [search, onSearchChange],
  );

  const onSelectView = useCallback(
    (view: BillingView) => {
      setCursorStack([]);
      // Each view has its own filters; carrying one set into the other would
      // silently narrow a list nobody asked to narrow.
      onSearchChange({ view, cursor: undefined, event: undefined });
    },
    [onSearchChange],
  );

  const onNext = useCallback(() => {
    const next = active.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, search.cursor]);
    onSearchChange({ ...search, cursor: next });
  }, [active.data, search, onSearchChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      onSearchChange({ ...search, cursor: stack[stack.length - 1] });
      return stack.slice(0, -1);
    });
  }, [search, onSearchChange]);

  const onOpenEvent = useCallback(
    (event: string | undefined) => {
      onSearchChange({ ...search, event });
    },
    [search, onSearchChange],
  );

  const filterSpecs: readonly FilterSpec[] = useMemo(() => {
    if (isEvents) {
      return [];
    }

    return [
      {
        id: 'status',
        label: 'Status',
        value: search.status,
        options: SUBSCRIPTION_STATUSES.map((value) => ({ value, label: humanise(value) })),
        onChange: (status) => {
          setFilter({ status: status as BillingSearch['status'] });
        },
      },
      {
        id: 'store',
        label: 'Store',
        value: search.store,
        options: SUBSCRIPTION_STORES.map((value) => ({ value, label: humanise(value) })),
        onChange: (store) => {
          setFilter({ store: store as BillingSearch['store'] });
        },
      },
      {
        id: 'entitlement',
        label: 'Access',
        value: search.entitlement,
        options: [
          { value: 'premium', label: 'premium' },
          { value: 'free', label: 'free' },
        ],
        onChange: (entitlement) => {
          setFilter({ entitlement: entitlement as BillingSearch['entitlement'] });
        },
      },
    ];
  }, [isEvents, search, setFilter]);

  const rows: readonly TableRow[] = useMemo(() => {
    if (isEvents) {
      return (events.data?.items ?? []).map((event) => ({
        id: event.id,
        onOpen: () => {
          onOpenEvent(event.id);
        },
        cells: [
          <TextCell key="rcId" value={event.revenueCatEventId} mono />,
          <TextCell key="type" value={event.type} weight="medium" />,
          <TextCell
            key="user"
            // Not "unknown": the delivery named an app_user_id that was never
            // one of ours, which is a different and useful fact.
            value={event.userId === null ? null : (event.displayName ?? 'Unnamed')}
            sub={event.userId === null ? 'not a Steado user' : null}
          />,
          <TextCell key="when" value={formatDateTime(event.createdAt)} />,
          event.replayable ? (
            <BadgeCell key="replayable" label="replayable" tone="ok" />
          ) : (
            <TextCell key="replayable" value={event.replayBlockedReason} tone="warn" />
          ),
          <RowActionsCell key="actions">
            {event.replayable && can.mutate ? (
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  // Straight to the drawer rather than replaying from the row:
                  // a replay is deliberately not idempotent-guarded, so the
                  // payload it is about to re-apply has to be readable first.
                  onOpenEvent(event.id);
                }}
              >
                Re-apply
              </Button>
            ) : null}
          </RowActionsCell>,
        ],
      }));
    }

    return (subscriptions.data?.items ?? []).map((row) => ({
      id: row.userId,
      cells: [
        <PersonCell
          key="user"
          name={row.displayName}
          sub={row.phone}
          initials={(row.displayName ?? '??').slice(0, 2).toUpperCase()}
        />,
        <BadgeCell key="status" label={humanise(row.status)} tone={STATUS_TONES[row.status]} dot />,
        <div key="entitlement" className="flex min-w-0 flex-col items-start gap-1">
          <BadgeCell label={row.entitlement} tone={row.entitlement === 'premium' ? 'purple' : 'mute'} />
          {mismatchOf(row) === null ? null : <span className="text-xs text-warn-fg">{mismatchOf(row)}</span>}
        </div>,
        <TextCell key="store" value={row.store === null ? null : humanise(row.store)} sub={row.productId} />,
        <TextCell key="renews" {...nextOf(row)} />,
      ],
    }));
  }, [isEvents, subscriptions.data, events.data, onOpenEvent, can.mutate]);

  const isFiltered = isEvents
    ? search.search !== undefined || search.type !== undefined || search.userId !== undefined
    : search.search !== undefined ||
      search.status !== undefined ||
      search.store !== undefined ||
      search.entitlement !== undefined;

  const onReset = useCallback(() => {
    setCursorStack([]);
    onSearchChange({ view: search.view });
  }, [search.view, onSearchChange]);

  const pageOffset = cursorStack.length * BILLING_PAGE_SIZE;

  if (active.isError) {
    return (
      <ErrorState
        title="Could not load billing"
        message={active.error.message}
        onRetry={() => {
          void active.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1200px] flex-col gap-3">
      <ScopePills
        label="Billing view"
        scopes={BILLING_VIEWS.map((view) => ({
          id: view,
          label: VIEW_LABELS[view],
          ...(counts.data === undefined ? {} : { count: counts.data.billing[view] }),
        }))}
        value={search.view}
        onChange={onSelectView}
      />

      {/*
        One toolbar for both views. The placeholder names the id each view can
        actually be searched by: `subscription_events` has no relation to
        `users`, so a name typed on the events view would match nothing and say
        nothing about why.
      */}
      <TableToolbar
        searchValue={search.search ?? ''}
        searchPlaceholder={isEvents ? 'RevenueCat event id' : 'Subscriber name or phone'}
        onSearchChange={(value) => {
          setFilter({ search: value === '' ? undefined : value });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
      />

      <DataTable
        caption={isEvents ? 'Webhook events' : 'Subscriptions'}
        title={isEvents ? 'What RevenueCat sent' : 'Subscriptions'}
        columns={isEvents ? EVENT_COLUMNS : SUBSCRIPTION_COLUMNS}
        rows={rows}
        isLoading={active.isPending}
        emptyTitle={isEvents ? 'No deliveries' : isFiltered ? 'No matches' : 'No subscriptions'}
        emptyBody={
          isEvents
            ? 'Nothing has arrived from RevenueCat yet, or nothing matches this filter.'
            : isFiltered
              ? 'No subscription matches the current filters. Clear them to see everyone RevenueCat has told us about.'
              : 'Nobody has subscribed yet. Rows appear here as RevenueCat’s webhook reports them.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" size="sm" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={active.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={active.isPending}
          />
        }
      />

      {search.event === undefined ? null : (
        <BillingEventDrawer
          eventId={search.event}
          onClose={() => {
            onOpenEvent(undefined);
          }}
        />
      )}
    </div>
  );
}
